import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { CheckOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { FiLink } from 'react-icons/fi';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { toEventTime, tryParse } from 'utils';
import sanitizeDescription from 'components/ChecklistEditor/sanitizeDescription';
import ProgressRing from './ProgressRing';
import { previewModel } from './checklistPreviewModel';
import './ChecklistPreview.scss';

const PREVIEW_ITEM_COUNT = 3;
const BRAND_FALLBACK = '#474747';

// Mirrors mobile's accentColor.
const accentColor = theme => theme?.brandSecondary || theme?.brandPrimary || BRAND_FALLBACK;

// Mirrors mobile's hasLink.
const hasLink = onPress => {
	const target = typeof onPress === 'string' ? tryParse(onPress) : onPress;
	return Boolean(target?.link || target?.route || target?.openApp);
};

const day = (event, timestamp) => toEventTime(event, timestamp).format('ddd, MMM D');

const Description = ({ html }) => {
	const safe = useMemo(() => sanitizeDescription(html), [html]);
	return safe ? <span className="cl-desc" dangerouslySetInnerHTML={{ __html: safe }} /> : null;
};

const CheckBadge = ({ checked }) => (
	<span className={`cl-badge ${checked ? 'cl-badge-checked' : ''}`}>
		{checked && (
			<svg viewBox="0 0 16 16">
				<path
					d="M3.5 8.5l3 3 6-7"
					fill="none"
					strokeWidth={2.2}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		)}
	</span>
);

// The builder has no attendee, so every item reads as not done.
const PreviewItem = ({ item }) => (
	<div className="cl-item">
		<CheckBadge />
		<span className="cl-text">
			{item.text}
			<Description html={item.description} />
		</span>
		{hasLink(item.on_press) && <FiLink className="cl-link" aria-hidden />}
	</div>
);

const SectionGroup = ({ section, event, showRing = true }) => {
	const open = !section.collapsed;
	const total = section.items.length;
	return (
		<div
			className={`cl-group ${open ? 'cl-group-open' : 'cl-group-folded'} ${
				section.upcoming ? 'cl-group-upcoming' : ''
			}`}
		>
			<div className="cl-group-header">
				{showRing && (
					<ProgressRing done={0} total={total} size={28} strokeWidth={4} className="cl-ring-sm" />
				)}
				<span className="cl-group-name">
					{section.name}
					{open && <Description html={section.description} />}
				</span>
				{section.upcoming ? (
					<span className="cl-group-toggle">{`Shows up ${day(event, section.appears_at)}`}</span>
				) : (
					<span className="cl-group-toggle">
						{`0 of ${total}`}
						{open ? (
							<IoChevronUp className="cl-chevron cl-chevron-up" aria-hidden />
						) : (
							<IoChevronDown className="cl-chevron cl-chevron-down" aria-hidden />
						)}
					</span>
				)}
			</div>
			{open && section.items.map(entry => <PreviewItem key={entry.id} item={entry} />)}
		</div>
	);
};

const Header = ({ title, subtitle, description, ring }) => (
	<div className="cl-header">
		{ring}
		<div className="cl-titles">
			<div className="cl-title">{title}</div>
			{subtitle && <div className="cl-subtitle">{subtitle}</div>}
			{description && <Description html={description} />}
		</div>
	</div>
);

const CaughtUp = ({ name }) => (
	<>
		<Header
			title={name}
			subtitle="Nothing due right now"
			ring={<ProgressRing done={1} total={1} size={52} strokeWidth={6} className="cl-ring-lg" />}
		/>
		<div className="cl-list">
			<div className="cl-item">
				<CheckBadge checked />
				<span className="cl-text cl-caught-up">You're all caught up</span>
			</div>
		</div>
	</>
);

const Current = ({ name, model }) => {
	const [only] = model.sections;
	const ring = (
		<ProgressRing
			done={0}
			total={model.currentTotal}
			size={52}
			strokeWidth={6}
			className="cl-ring-lg"
			label={`0/${model.currentTotal}`}
		/>
	);
	return model.sections.length === 1 ? (
		<>
			<Header title={name} subtitle={only.name} description={only.description} ring={ring} />
			<div className="cl-list">
				{only.items.map(entry => (
					<PreviewItem key={entry.id} item={entry} />
				))}
			</div>
		</>
	) : (
		<>
			<Header title={name} ring={ring} />
			{model.sections.map(section => (
				<SectionGroup key={section.id} section={section} showRing={false} />
			))}
		</>
	);
};

const All = ({ name, model, event }) => (
	<>
		<div className="cl-title cl-title-all">{name}</div>
		{model.sections.map(section => (
			<SectionGroup key={section.id} section={section} event={event} />
		))}
	</>
);

const ChecklistPreview = props => {
	const { from, item, margin, checklists, loaded, loadFailed, event } = props;
	const checklistId = item?.props?.checklist_id;
	const checklist = checklists.find(c => c.id === checklistId);
	const { show, full_checklist_on_press } = item?.props ?? {};
	const model = checklist && previewModel(checklist.content, show, new Date());

	const all = show === 'all';
	const showsCard =
		model?.state === 'list' || model?.state === 'caughtUp' || (all && model?.state === 'hidden');

	const renderTile = () => (
		<div className="checklist-tile">
			<div className="tile-title">Checklist</div>
			{[...Array(PREVIEW_ITEM_COUNT)].map((_, i) => (
				<div className="tile-row" key={i}>
					<span className="box" />
					<span className="line" />
				</div>
			))}
		</div>
	);

	const renderHints = () => (
		<>
			{model.state === 'empty' && (
				<div className="cl-note">No items yet. Edit the checklist to add some.</div>
			)}
			{model.state === 'hidden' && (
				<div className="cl-note">Attendees don't see this widget yet.</div>
			)}
			{(model.state === 'caughtUp' || model.state === 'hidden') && !all && model.next && (
				<div className="cl-next">{`Next: ${model.next.name} shows up ${day(
					event,
					model.next.appears_at
				)}`}</div>
			)}
		</>
	);

	const renderCard = () => (
		<div className={`cl-card ${all ? 'cl-card-all' : ''}`}>
			{model.state === 'caughtUp' ? (
				<CaughtUp name={checklist.name} />
			) : all ? (
				<All name={checklist.name} model={model} event={event} />
			) : (
				<Current name={checklist.name} model={model} />
			)}
			{hasLink(full_checklist_on_press) && (
				<div className="cl-more" style={{ color: accentColor(event?.theme) }}>
					View full checklist
				</div>
			)}
		</div>
	);

	const renderEmpty = () => (
		<div className="cl-card cl-empty" style={{ margin }}>
			{!loaded ? (
				<Spin />
			) : (
				<>
					<CheckOutlined />
					<span>
						{loadFailed
							? "Couldn't load this event's checklists"
							: 'Pick a checklist in the options panel'}
					</span>
				</>
			)}
		</div>
	);

	return (
		<div className="WidgetPreviews">
			<div className={!from ? 'hover-wrap' : null}>
				{!from && <div className="hover-container">Checklist</div>}
				{from ? (
					renderTile()
				) : checklist ? (
					<div style={{ margin }}>
						{showsCard && renderCard()}
						{renderHints()}
					</div>
				) : (
					renderEmpty()
				)}
			</div>
		</div>
	);
};

const msp = ({ checklists, events }) => ({
	checklists: checklists.checklists,
	loaded: checklists.loaded,
	loadFailed: checklists.loadFailed,
	event: events.currentEvent,
});

export default connect(msp, null)(ChecklistPreview);
